import { useState, useEffect, useMemo, Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings } from '@/utils/companySettings';
import { 
  FolderOpen, Settings, Layers, Plus, Phone, MapPin, Clock,
  CheckCircle2, Circle, ChevronRight, ExternalLink, Users, MoreVertical,
  Archive, Trash2, RefreshCw, AlertTriangle, MessageSquare, ListTodo, DollarSign,
  Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AppShell from '@/components/layout/AppShell';
import ShellMasthead from '@/components/layout/ShellMasthead';
import { useOwnName } from '@/hooks/useOwnName';
import AIChadGreeting from '@/components/ai-chad/AIChadGreeting';
import MastheadBand from '@/components/dashboard/MastheadBand';
import { portfolioSentence } from '@/components/dashboard/mastheadCopy';
import '@/styles/desk-bands.css';
import '@/styles/kmd-sheet.css';

// RS-10 — whose answer a row is waiting for, when this position cannot give it.
const OWED_ROLE = {
  question: 'whoever may answer',
  vendor_reply: 'whoever may answer',
  submittal: 'whoever may stamp',
  confirmed: 'whoever may stamp',
  returned: 'whoever may edit the project',
  release: 'whoever may release to the client',
};
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import CreateProjectForm from '@/components/CreateProjectForm';
import HelpIcon from '@/components/HelpIcon';
import { getCurrentUserId } from '../utils/currentUser';
import { useRole } from '@/contexts/RoleContext';
import SetProjectImageDialog from '@/components/SetProjectImageDialog';
import { ProjectPlate } from '@/components/projects/ProjectPlate';
import { phaseShort } from '@/utils/phaseShort';
import { currentPhaseName, phaseCells, phaseCellKey, cellSlug, NO_PHASE_CELL } from '@/utils/phaseNumbering';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASES = [
  { number: 1, name: 'PreDesign', short: 'PD' },
  { number: 2, name: 'Schematic', short: 'SD' },
  { number: 3, name: 'Design Dev', short: 'DD' },
  { number: 4, name: 'Construction Docs', short: 'CD' },
  { number: 5, name: 'Bidding', short: 'BID' },
  { number: 6, name: 'Construction', short: 'CA' }
];
// DP103 ruling 2d — THE LIST ABOVE NO LONGER SPEAKS FOR ANYTHING. It labelled
// the six filter cells, the band count and the empty state, and its names
// disagreed with Chad's eight-phase projects: cell 5 read "Bidding" where four
// of them say "Contract Negotiation", and phases 0 and 7 had no cell at all.
// The cells come from phaseCells() now. Kept, unread, until Chad rules on
// deleting it — same standing as UserDashboard.js's copy.
void PHASES;

/**
 * DP41 — the plate's phase line comes from the PROJECT'S OWN PHASES, which are
 * the copy of the template its type selected. The fixed six-name list above is
 * a lifecycle shorthand used by the phase filter row and must never speak for a
 * project whose template runs eight phases, or two.
 *
 * DP103 ruling 1b — and it is no longer a copy. The one resolver lives in
 * utils/phaseNumbering.js, read by this page, My Desk and the catalogue card;
 * this export is kept so nothing that imports it by name has to move.
 */
export const templatePhaseOf = currentPhaseName;

function Dashboard() {
  const ownGreetingName = useOwnName().fullName;  // DP63 Part 2
  const [projects, setProjects] = useState([]);
  const [clipStats, setClipStats] = useState({ total: 0, unassigned: 0 });  const [projectIndicators, setProjectIndicators] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  // DP81 Part 3 — which of the four loads failed, by name. Empty object means
  // every one answered; an empty portfolio is then a real, reported state.
  const [loadFailures, setLoadFailures] = useState({});
  // DP82 Part 1 — the phase filter. The strip had no click handler at all; its
  // six cells were a read-only count. null = no filter.
  const [activePhase, setActivePhase] = useState(null);
  // DP82 Part 6 — decisions waiting across the tenant, from the one endpoint
  // DP81 added. The masthead tally and card A.1 read THE SAME state, so the
  // number in the band and the rows in the card can never disagree.
  const [decisions, setDecisions] = useState(null);
  // RS-10 — the paper waiting on the firm: questions, submittals, releases.
  const [owed, setOwed] = useState(null);
  const [tenantName, setTenantName] = useState('');
  const [showNewProject, setShowNewProject] = useState(false);

  // ── AI Chad banner data (portfolio-scoped) ────────────────────────
  const [allTasks, setAllTasks] = useState([]);
  const [leadsCount, setLeadsCount] = useState('—');
  const [leads, setLeads] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);

  // ── Add-photo invite (placeholder cards) ──────────────────────────
  // Tenant brand tint comes from the caller's own company settings.
  const [brandColor, setBrandColor] = useState(null);
  const [photoProject, setPhotoProject] = useState(null);
  const { role } = useRole();
  const canEditPhotos = role !== 'client' && role !== 'contractor';

  useEffect(() => {
    getCompanySettings()
      .then((d) => setBrandColor(d?.primary_color || null))
      .catch(() => {});
  }, []);
  const darkMode = (() => {
    try { return localStorage.getItem('keyMadeDarkMode') === 'true'; } catch { return false; }
  })();

  useEffect(() => {
    axios.get(`${API}/tasks`)
      .then(r => setAllTasks(r.data.tasks || r.data || []))
      .catch(() => setAllTasks([]));
  }, []);

  useEffect(() => {
    axios.get(`${API}/crm/leads`)
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.leads || []);
        const active = arr.filter(l => l.stage !== 'lost' && l.stage !== 'converted' && l.stage !== 'closed');
        setLeadsCount(active.length);
        setLeads(arr);
      })
      .catch(() => { /* leave em-dash placeholder */ });
    // DP104 Part 4b — THE STAGES ARE READ, in the order the endpoint returns
    // them. No count is assumed and no stage is named in this file.
    axios.get(`${API}/crm/pipeline/stages`)
      .then(r => setPipelineStages(r.data?.stages || []))
      .catch(() => setPipelineStages([]));
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      // DP81 Part 3 — A FAILED FETCH NOW REPORTS A FAILURE.
      // All of these used to catch and return an empty shape, so a failed load
      // looked identical to an empty portfolio: no projects, no clips, no
      // indicators, and no way for Chad to know whether that was the truth or a
      // broken request. Same shape as the is_archived query that hid his Yard
      // from its own listing. Each fetch now records WHICH ONE failed and why.
      // DP103 ruling 2 — there were FOUR. `/projects/by-phase` is gone from
      // this page: the strip counts the projects already on it, so the cells
      // and the plates they filter to cannot disagree.
      const failures = {};
      const [projectsRes, clipsRes, indicatorsRes] = await Promise.all([
        axios.get(`${API}/projects`).catch((err) => { failures.projects = err.message || 'request failed'; return null; }),
        axios.get(`${API}/clips`).catch((err) => { failures.clips = err.message || 'request failed'; return null; }),
        axios.get(`${API}/projects/indicators`).catch((err) => { failures.indicators = err.message || 'request failed'; return null; }),
      ]);

      setLoadFailures(failures);

      // DP82 — the decisions endpoint and the tenant's own name for the
      // masthead subline. Neither can empty the page, so a failure here leaves
      // the tally reading a dash rather than taking the whole page down.
      axios.get(`${API}/decisions/awaiting`)
        .then(({ data }) => setDecisions(data))
        .catch(() => setDecisions(null));
      axios.get(`${API}/paper/owed`)
        .then(({ data }) => setOwed(data))
        .catch(() => setOwed(null));
      getCompanySettings()
        .then((d) => setTenantName(d?.company_name || ''))
        .catch(() => setTenantName(''));

      // Projects failing is the one that empties the page, so it takes the
      // page-level failure state at :402 rather than a strip.
      if (failures.projects) {
        setFetchError(failures.projects);
        setProjects([]);
      } else {
        const rawProjects = Array.isArray(projectsRes.data) ? projectsRes.data : (projectsRes.data?.projects || []);
        setProjects(rawProjects);
      }

      setProjectIndicators(failures.indicators ? {} : (indicatorsRes.data?.indicators || {}));
      if (failures.clips) {
        setClipStats(null);
      } else {
        const allClips = Array.isArray(clipsRes.data) ? clipsRes.data : [];
        setClipStats({ total: allClips.length, unassigned: allClips.filter(c => !c.project_id).length });
      }

      const broken = Object.keys(failures);
      if (broken.length) {
        console.error('DP81 — these loads failed:', failures);
        toast.error(broken.length === 1
          ? `Could not load ${broken[0]}`
          : `Could not load: ${broken.join(', ')}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setFetchError(error.message || 'Failed to load data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCreated = (project) => {
    setShowNewProject(false);
    fetchData();
    window.location.href = `/projects/${project.id}/hub`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'on_hold': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'completed': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    // Clean the phone number
    const cleaned = phone.replace(/\D/g, '');
    return `tel:${cleaned}`;
  };

  const getGoogleMapsUrl = (address) => {
    if (!address || !address.street) return null;
    const query = encodeURIComponent(
      `${address.street}, ${address.city}, ${address.state} ${address.zip_code || ''}`
    );
    return `https://maps.google.com/?q=${query}`;
  };

  // Group projects by status. Feb 2026 — the super-admin test fixture
  // (is_test_fixture: true) always sorts to the END of each group so real
  // work leads. Stable sort: preserves prior order among real projects.
  const _fixtureLast = (a, b) => Number(!!a.is_test_fixture) - Number(!!b.is_test_fixture);
  const activeProjects = projects.filter(p => p.status === 'active' || !p.status).sort(_fixtureLast);
  /* DP103 ruling 2 — the filter selects on the project's OWN resolved phase,
     the same value the plate prints and the same value the cell counts, so a
     cell's figure and the plates it shows can never disagree. It used to read
     `current_phase` against six cells numbered 1-6, which no eight-phase or
     two-phase project can be represented by. `activePhase` is a cell key now,
     not a number. */
  const cells = phaseCells(activeProjects);
  const activeCell = cells.find((c) => c.key === activePhase) || null;
  /* DP104 Part 3d — WHAT MAKES A PROJECT OVERDUE. There is no project-level due
     date and /api/projects/indicators carries no overdue field, so the only
     source is the project's own overdue TASKS: a due date in the past, not
     completed — the same test band A's Overdue tally uses (line 455), read
     once here and grouped by project. */
  const overdueByProject = useMemo(() => {
    const now = new Date();
    const by = {};
    (allTasks || []).forEach((t) => {
      if (!t.project_id || !t.due_date || t.status === 'completed') return;
      if (new Date(t.due_date) < now) by[t.project_id] = (by[t.project_id] || 0) + 1;
    });
    return by;
  }, [allTasks]);
  /* DP104 Part 5 — THE STRIP SITS DIRECTLY UNDER THE MASTHEAD, before band A,
     because it filters everything below it. It was measured at y=939, below
     band A. Held in a variable so it can be rendered inside the masthead
     fragment without restructuring the page: the order is masthead · phase
     strip · band A · band B and the plates · band C · the closing note. */
  const phaseStrip = (
    <div className="mb-8" data-testid="phase-strip-block">
      <h2 className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">
        Project Phases
      </h2>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="phase-strip">
        {cells.length === 0 && (
          <p className="text-xs text-muted-foreground col-span-3 sm:col-span-6"
             data-testid="phase-strip-empty">
            No active projects to place in a phase.
          </p>
        )}
        {cells.map((cell) => {
          const on = activePhase === cell.key;
          const slug = cellSlug(cell.key);
          return (
            <button
              type="button"
              key={cell.key}
              data-testid={`phase-cell-${slug}`}
              data-phase-number={cell.number ?? ''}
              aria-pressed={on}
              title={cell.key === NO_PHASE_CELL
                ? 'Projects whose phase cannot be resolved from their own phases'
                : `Phase ${cell.number} of this project's own template`}
              /* DP82 Part 1a — click filters, click the same one again
                 clears. Part 1b — the active column keeps a brand-colour
                 top border. */
              onClick={() => setActivePhase(on ? null : cell.key)}
              className={`border p-4 text-center transition-all cursor-pointer border-t-2 ${
                on ? 'bg-card' : cell.count > 0 ? 'border-accent bg-accent/5' : 'border-border bg-card'
              }`}
              style={on ? { borderTopColor: 'var(--km-brand)' } : { borderTopColor: 'transparent' }}
            >
              <p className="text-xs text-muted-foreground mb-1">{cell.short}</p>
              <p className={`text-2xl font-heading ${cell.count > 0 ? 'text-accent' : 'text-muted-foreground'}`}
                 data-testid={`phase-count-${slug}`}>
                {cell.count}
              </p>
              <p className="text-xs text-muted-foreground hidden md:block mt-1">
                {cell.name}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
  const phaseFilteredProjects = activePhase === null
    ? activeProjects
    : activeProjects.filter((p) => phaseCellKey(p) === activePhase);
  const archivedProjects = projects.filter(p => p.status === 'archived').sort(_fixtureLast);
  const otherProjects = projects.filter(p => p.status && p.status !== 'active' && p.status !== 'archived').sort(_fixtureLast);

  // Project actions
  const handleArchiveProject = async (projectId) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { status: 'archived' });
      toast.success('Project archived');
      fetchData();
    } catch (error) {
      console.error('Error archiving project:', error);
      toast.error('Failed to archive project');
    }
  };

  const handleRestoreProject = async (projectId) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { status: 'active' });
      toast.success('Project restored');
      fetchData();
    } catch (error) {
      console.error('Error restoring project:', error);
      toast.error('Failed to restore project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Project deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  // DP60 Part 1d — All Projects' three header actions MOVED into the shell's
  // top-strip slot. Same buttons, same handlers, same dialog — relocated, not
  // rebuilt, and no duplicate left in the page header.
  const pageActions = (
    <>
      {/* DP81 Part 1b — the help icon IS page specific (elementId
          "projects-list"), so it joins the top strip's page actions rather than
          going into the shell. */}
      <HelpIcon elementId="projects-list" position="inline" />
      <Button variant="outline" size="sm" onClick={() => window.location.href = '/contacts'} className="text-xs sm:text-sm h-9 w-9 p-0 sm:h-auto sm:w-auto sm:px-3" aria-label="Contacts" data-testid="projects-contacts-btn">
        <Users className="w-4 h-4 sm:mr-2" />
        <span className="hidden sm:inline">Contacts</span>
        <span className="sr-only">People</span>
      </Button>
      <Button variant="outline" onClick={() => window.location.href = '/clips'} size="sm" className="text-xs sm:text-sm h-9 w-9 p-0 sm:h-auto sm:w-auto sm:px-3 relative" aria-label="Clips library" data-testid="projects-clips-library-btn">
        <Layers className="w-4 h-4 sm:mr-2" />
        <span className="hidden sm:inline">Clips Library</span>
        <span className="sr-only">Clips</span>
        <Badge variant="secondary" className="absolute -top-2 -right-2 px-1 sm:static sm:ml-2 sm:px-2.5">{clipStats ? clipStats.total : '—'}</Badge>
      </Button>
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogTrigger asChild>
          <Button data-testid="new-project-button" size="sm" aria-label="New project" className="bg-[var(--km-brand)] hover:opacity-90 text-white text-xs sm:text-sm h-9 w-9 p-0 sm:h-auto sm:w-auto sm:px-3">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Project</span>
            <span className="sr-only">New</span>
          </Button>
        </DialogTrigger>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="new-project-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Create New Project</DialogTitle>
            <DialogDescription>
              Set up a new project with client and scope details. Project code will be auto-generated.
            </DialogDescription>
          </DialogHeader>
          <CreateProjectForm
            onSuccess={handleProjectCreated}
            onCancel={() => setShowNewProject(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );

  return (
    <AppShell page="projects" shellTestId="all-projects-page" topStripActions={pageActions}>
    {/* DP90 Part 1b — this div was `bg-background`, a warm tan, and it is the
        content region's only child: 3019px tall, covering the shell's own
        surface completely. The surface belongs to .km-shell__content. */}
    <div>
      {/* DP81 Part 1 — the page's own sticky header is GONE. It drew a second
          title bar ("All Projects", the wordmark) inside AppShell, which
          already supplies the breadcrumb and the top strip; Chad saw two
          stacked. The page-specific help icon moved into the top strip's
          actions; GlobalActionItems moved into the shell itself, because it is
          portfolio-wide and belongs beside presence and notifications. */}

      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 md:px-12 py-4 sm:py-8 overflow-x-hidden">
        {/* DP81 Part 3b — the three loads that do not empty the page get their
            own line. A missing phase strip or a missing indicator is now a
            reported fault instead of a zero that looks like the truth. */}
        {['phases', 'indicators', 'clips'].some((k) => loadFailures[k]) && (
          <div
            className="mb-4 sm:mb-6 border border-[var(--km-late)] bg-[var(--km-late)]/10 px-3 py-2 flex items-start gap-2"
            data-testid="projects-partial-load-failure"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[var(--km-late)]" />
            <div className="text-xs">
              <p className="font-medium">Something did not load.</p>
              <ul className="text-muted-foreground mt-0.5 space-y-0.5">
                {loadFailures.phases && <li data-testid="load-failure-phases">Phase counts — {loadFailures.phases}. The strip below is not the real count.</li>}
                {loadFailures.indicators && <li data-testid="load-failure-indicators">Project indicators — {loadFailures.indicators}. Plates are missing their flags.</li>}
                {loadFailures.clips && <li data-testid="load-failure-clips">Clips — {loadFailures.clips}. The Clips Library count reads a dash.</li>}
              </ul>
              <button
                type="button"
                onClick={fetchData}
                className="mt-1 underline underline-offset-2 hover:no-underline"
                data-testid="projects-partial-retry"
              >
                Try again
              </button>
            </div>
          </div>
        )}
        {/* AI Chad Greeting — portfolio context */}
        {(() => {
          const now = new Date();
          const todayStr = now.toDateString();
          const overdueList = (allTasks || []).filter(
            (t) => t.due_date && new Date(t.due_date) < now && t.status !== 'completed'
          );
          const todayList = (allTasks || []).filter((t) => {
            if (!t.due_date || t.status === 'completed') return false;
            return new Date(t.due_date).toDateString() === todayStr;
          });
          const completedTodayList = (allTasks || []).filter((t) => {
            if (t.status !== 'completed') return false;
            const at = t.completed_at || t.updated_at;
            return at && new Date(at).toDateString() === todayStr;
          });
          const portfolioTodayTasks = {
            overdue: overdueList,
            today: todayList,
            completed: completedTodayList,
          };
          // DP82 Part 4c — the four tallies MOVED into the masthead. They are
          // built here because this is where the lists already exist, and
          // handed to MastheadBand. The AI Chad strip is passed none, so the
          // same number is never printed twice.
          const decisionCount = decisions ? decisions.count : null;
          const owedCount = owed ? owed.count : null;
          const figures = [
            { label: 'Active', value: activeProjects.length, tone: 'default' },
            { label: 'Overdue', value: overdueList.length, tone: 'danger' },
            { label: 'Decisions', value: decisionCount === null ? '\u2014' : decisionCount, tone: 'warning' },
            { label: 'Leads', value: leadsCount, tone: 'default' },
          ];
          // DP82 Part 4a — the headline reads the SAME two sources as the
          // tallies: overdue tasks and decisions waiting, counted once.
          const needing = overdueList.length + (decisionCount || 0);
          const today = new Date().toLocaleDateString(undefined,
            { day: 'numeric', month: 'long', year: 'numeric' });
          return (
            <>
              {/* DP82 Part 4 — REUSED: components/dashboard/MastheadBand.js,
                  styles/mydesk-surface.css. Same band, same planes/flat/image
                  background from the same `masthead_background` company
                  setting, same scrim, same figure and quote treatment. Four
                  optional props were added to it rather than writing a second
                  masthead. AI Chad is passed as an empty greeting column
                  because on this page he lives in card A.2. */}
              <ShellMasthead>
              <MastheadBand
                testId="projects-masthead"
                page="projects"
                user={{ name: ownGreetingName, id: getCurrentUserId() }}
                darkMode={darkMode}
                figures={figures}
                todayTasks={portfolioTodayTasks}
                events={[]}
                greeting={null}
                sentence={portfolioSentence(activeProjects.length, needing)}
                subline={['All Projects', tenantName, today].filter(Boolean).join(' \u00B7 ')}
              />
              </ShellMasthead>

              {/* DP104 Part 5 — directly under the masthead, before band A. */}
              {phaseStrip}

              {/* DP82 Part 5 — REUSED: the .kmd-bandhead markup and
                  styles/desk-bands.css, exactly as FreeformDashboard.js:1294
                  draws them on My Desk and the Work Table home. */}
              <div className="kmd-bandhead" data-testid="band-head-A">
                <span className="kmd-bandhead__key">A</span>
                <span className="kmd-bandhead__name">Needs you</span>
                <span className="kmd-bandhead__count" data-testid="band-count-A">
                  {decisionCount === null ? 'decisions unavailable'
                    : `${decisionCount} decision${decisionCount === 1 ? '' : 's'}`}
                  {owedCount === null ? '' : ` · ${owedCount} owed`}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-8">
                {/* A.1 — decisions waiting.
                    DP82 Part 6 — REUSED: the .kmd-sheet treatment from
                    styles/kmd-sheet.css, the same one My Desk's cards and
                    AiChadCard.js use. Only the top-border colour is set here,
                    from --km-late, so no raw colour enters this file. */}
                <article
                  className="lg:col-span-8 kmd-sheet"
                  style={{ borderTopWidth: '2px', borderTopColor: 'var(--km-late)' }}
                  data-testid="card-A1-decisions"
                >
                  <div className="kmd-sheet__block">
                    <span className="kmd-sheet__series" style={{ color: 'var(--km-late)' }}>A.1</span>
                    <span className="kmd-sheet__name">Decisions waiting</span>
                    <span className="kmd-sheet__meta" data-testid="card-A1-count">
                      {decisionCount === null ? 'unavailable' : `${decisionCount} open`}
                    </span>
                  </div>
                  <div className="kmd-sheet__body">
                    {decisionCount === null ? (
                      <p className="text-sm text-muted-foreground" data-testid="card-A1-failed">
                        Could not load decisions. This is a failed request, not an empty list.
                      </p>
                    ) : decisionCount === 0 ? (
                      <p className="text-sm text-muted-foreground" data-testid="card-A1-empty">
                        Nothing is waiting on you.
                      </p>
                    ) : decisions.decisions.map((d, i) => (
                      <div
                        key={d.id || i}
                        className={`grid grid-cols-[1fr_auto] gap-2 py-2 ${i < decisions.decisions.length - 1 ? 'border-b border-dashed border-border' : ''}`}
                        data-testid={`decision-row-${d.id || i}`}
                      >
                        <span>
                          {/* DP82 Part 6 — the project's OWN colour. A project
                              with none falls back to the muted ink token, NOT
                              to another project's colour and NOT to the status
                              red, which would read as overdue. */}
                          <span
                            className="block font-mono text-[11px] tracking-[0.06em]"
                            style={{ color: d.project_color || 'var(--km-ink-3)' }}
                            data-testid={`decision-project-${d.id || i}`}
                          >
                            {d.project_name || 'No project'}
                          </span>
                          <span className="font-heading text-base leading-snug">{d.decision}</span>
                        </span>
                        {/* DP82 Part 2c — no due date means NO overdue line,
                            rather than a wrong one. */}
                        <span className="text-right">
                          {d.days_over === null || d.days_over === undefined ? null : (
                            <span
                              className="font-mono text-[9px] uppercase tracking-[0.12em]"
                              style={{ color: 'var(--km-late)' }}
                              data-testid={`decision-due-${d.id || i}`}
                            >
                              {d.days_over > 0 ? `${d.days_over} day${d.days_over === 1 ? '' : 's'} over`
                                : d.days_over === 0 ? 'Today'
                                : `in ${-d.days_over} day${d.days_over === -1 ? '' : 's'}`}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </article>

                {/* A.2 — AI Chad. Content kept exactly as it was on this page
                    (screen="projects"); the treatment is My Desk's own
                    `kmd-sheet--ink`, the one dark object on a light page. */}
                <article
                  className="lg:col-span-4 kmd-sheet kmd-sheet--ink kmd-greetingtype"
                  data-km-surface="dark"
                  style={{ borderTopWidth: '2px', borderTopColor: 'var(--km-key)' }}
                  data-testid="card-A2-ai-chad"
                >
                  <div className="kmd-sheet__block">
                    <span className="kmd-sheet__series" style={{ color: 'var(--km-key)' }}>A.2</span>
                    <span className="kmd-sheet__name">AI Chad</span>
                    <span className="kmd-sheet__meta">across your projects</span>
                  </div>
                  <div className="kmd-sheet__body" data-testid="dashboard-ai-chad-greeting">
                    <AIChadGreeting
                      /* DP63 Part 2 — was userName="Chad Holgerson", which greeted every
                         user as Chad. Authoritative profile; blank if unresolved. */
                      userName={ownGreetingName}
                      userId={getCurrentUserId()}
                      screen="projects"
                      darkMode={true}
                      todayTasks={portfolioTodayTasks}
                      calendarEvents={[]}
                      tallies={[]}
                      /* DP104 Part 6 — A.2 IS AI CHAD ONLY. It was drawing the
                         clock, the weather, three tallies and a second quote
                         while the masthead above already carried four tallies
                         and a different quote. `chatOnly` is the SAME prop the
                         Project Desk's A.2 passes (AiChadCard.js:59) — the lead
                         line, the secondary line, the actions and the ask box,
                         and nothing else. Not a new mechanism. */
                      chatOnly
                    />
                  </div>
                </article>

                {/* A.3 — RS-10: the paper waiting on the FIRM. The app has
                    always said what is waiting on the parties; this says what
                    they are waiting on us for. Same .kmd-sheet markup as A.1,
                    the wait colour rather than the late one. */}
                <article
                  className="lg:col-span-12 kmd-sheet"
                  style={{ borderTopWidth: '2px', borderTopColor: 'var(--km-wait)' }}
                  data-testid="card-A3-owed"
                >
                  <div className="kmd-sheet__block">
                    <span className="kmd-sheet__series" style={{ color: 'var(--km-wait)' }}>A.3</span>
                    <span className="kmd-sheet__name">Answers you owe</span>
                    <span className="kmd-sheet__meta" data-testid="card-A3-count">
                      {owedCount === null ? 'unavailable' : `${owedCount} owed`}
                    </span>
                  </div>
                  <div className="kmd-sheet__body">
                    {owedCount === null ? (
                      <p className="text-sm text-muted-foreground" data-testid="card-A3-failed">
                        Could not load the paper.
                      </p>
                    ) : owedCount === 0 ? (
                      <p className="text-sm text-muted-foreground" data-testid="card-A3-empty">
                        No answers are owed.
                      </p>
                    ) : owed.owed.map((row, i) => (
                      <div
                        key={row.id || i}
                        className={`grid grid-cols-[1fr_auto] gap-2 py-2 ${i < owed.owed.length - 1 ? 'border-b border-dashed border-border' : ''}`}
                        data-testid={`owed-row-${row.id || i}`}
                      >
                        <span>
                          <span
                            className="block font-mono text-[11px] tracking-[0.06em]"
                            style={{ color: row.project_color || 'var(--km-ink-3)' }}
                            data-testid={`owed-project-${row.id || i}`}
                          >
                            {row.project_name || 'No project'}
                          </span>
                          <span className="font-heading text-base leading-snug">{row.title}</span>
                          {row.who ? (
                            <span className="block text-sm text-muted-foreground"
                              data-testid={`owed-who-${row.id || i}`}>{row.who}</span>
                          ) : null}
                        </span>
                        <span className="text-right">
                          {row.days_over === null || row.days_over === undefined ? null : (
                            <span
                              className="block font-mono text-[9px] uppercase tracking-[0.12em]"
                              style={{ color: 'var(--km-late)' }}
                              data-testid={`owed-due-${row.id || i}`}
                            >
                              {row.days_over > 0 ? `${row.days_over} day${row.days_over === 1 ? '' : 's'} over`
                                : row.days_over === 0 ? 'Today'
                                : `in ${-row.days_over} day${row.days_over === -1 ? '' : 's'}`}
                            </span>
                          )}
                          {row.can_act ? (
                            <Link to={row.link} className="kmd-sheet__link"
                              data-testid={`owed-act-${row.id || i}`}>
                              {row.act_label}
                            </Link>
                          ) : (
                            <span className="block text-sm text-muted-foreground"
                              data-testid={`owed-waiting-${row.id || i}`}>
                              {`Waiting for ${OWED_ROLE[row.kind] || 'the office'}`}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </article>
              </div>

              {/* DP82 Part 5 — band B */}
              <div className="kmd-bandhead" data-testid="band-head-B">
                <span className="kmd-bandhead__key">B</span>
                <span className="kmd-bandhead__name">The work</span>
                <span className="kmd-bandhead__count" data-testid="band-count-B">
                  {activePhase === null ? 'all phases'
                    : `${activeCell?.name || activePhase} only`}
                </span>
              </div>
            </>
          );
        })()}

        {loading ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">Loading projects...</p>
          </div>
        ) : fetchError ? (
          <div className="text-center py-24">
            <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-yellow-500" />
            <h2 className="font-heading text-xl mb-2">Unable to load projects</h2>
            <p className="text-muted-foreground mb-4">{fetchError}</p>
            <p className="text-xs text-muted-foreground mb-4 max-w-md mx-auto">
              This is a failed request, not an empty portfolio. Your projects are
              still there.
            </p>
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-20 h-20 border border-border mb-6">
              <FolderOpen className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-2xl mb-2">No projects yet</h2>
            <p className="text-muted-foreground mb-6">Create your first project to get started</p>
            <Button onClick={() => setShowNewProject(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </div>
        ) : (
          <>
            {/* Active Projects */}
            {activeProjects.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-light mb-6 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Active Projects ({phaseFilteredProjects.length}{activePhase === null ? '' : ` of ${activeProjects.length}`})
                </h2>
                {/* DP82 Part 1 — a phase with no projects says so rather than
                    leaving the band empty and looking broken. */}
                {phaseFilteredProjects.length === 0 && (
                  <p className="text-sm text-muted-foreground mb-6" data-testid="phase-filter-empty">
                    No projects in {activeCell?.name || activePhase}.
                    <button type="button" onClick={() => setActivePhase(null)}
                            className="ml-2 underline underline-offset-2 hover:no-underline"
                            data-testid="phase-filter-clear">Show all phases</button>
                  </p>
                )}
                {/* DP104 Part 3 — the mockup's `.plates` grid, from projects-plate.css */}
                <div className="plates">
                  {phaseFilteredProjects.map((project) => (
                    <ProjectPlate
                      key={project.id}
                      project={project}
                      phaseLabel={templatePhaseOf(project)}
                      progress={projectIndicators[project.id]?.budget_percent || 0}
                      overdueCount={overdueByProject[project.id] || 0}
                      canEditPhotos={canEditPhotos}
                      onAddPhoto={setPhotoProject}
                      onArchive={handleArchiveProject}
                      onRestore={handleRestoreProject}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Archived Projects */}
            {archivedProjects.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-light mb-6 text-muted-foreground flex items-center gap-2">
                  <Archive className="w-5 h-5" />
                  Archived Projects ({archivedProjects.length})
                </h2>
                <div className="plates opacity-75">
                  {archivedProjects.map((project) => (
                    <ProjectPlate
                      key={project.id}
                      project={project}
                      phaseLabel={templatePhaseOf(project)}
                      progress={projectIndicators[project.id]?.budget_percent || 0}
                      overdueCount={overdueByProject[project.id] || 0}
                      canEditPhotos={canEditPhotos}
                      onAddPhoto={setPhotoProject}
                      onArchive={handleArchiveProject}
                      onRestore={handleRestoreProject}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Other Projects */}
            {otherProjects.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-light mb-6 text-muted-foreground">
                  Other Projects ({otherProjects.length})
                </h2>
                <div className="plates">
                  {otherProjects.map((project) => (
                    <ProjectPlate
                      key={project.id}
                      project={project}
                      phaseLabel={templatePhaseOf(project)}
                      progress={projectIndicators[project.id]?.budget_percent || 0}
                      overdueCount={overdueByProject[project.id] || 0}
                      canEditPhotos={canEditPhotos}
                      onAddPhoto={setPhotoProject}
                      onArchive={handleArchiveProject}
                      onRestore={handleRestoreProject}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Add-photo dialog (staff-only; opened from placeholder cards) */}
        <SetProjectImageDialog
          open={Boolean(photoProject)}
          onOpenChange={(open) => !open && setPhotoProject(null)}
          project={photoProject}
          field="primary_image"
          onSet={(url, thumb) => {
            const pid = photoProject?.id;
            setProjects(prev => prev.map(p => (p.id === pid
              ? { ...p, primary_image: url, primary_image_thumbnail: thumb }
              : p)));
            setPhotoProject(null);
          }}
        />

        {/* DP82 Part 5 — band C. Its card C.1 is Part 7d and is CARRIED, so
            the band currently states its count and nothing else. */}
        <div className="kmd-bandhead" data-testid="band-head-C">
          <span className="kmd-bandhead__key">C</span>
          <span className="kmd-bandhead__name">Pipeline</span>
          <span className="kmd-bandhead__count" data-testid="band-count-C">
            {leadsCount} lead{leadsCount === 1 ? '' : 's'}
          </span>
        </div>

        {/* DP104 Part 4 — C.1, PASTED from projects-markup.md. Missing since
            DP82, when Chad's six stages had not been settled.
            THE PACKAGE SHIPS NO CSS FOR `card c12 blk num t r bdy three fig
            figl` — projects-plate.css defines none of them and neither does any
            stylesheet in the app. Rather than invent values, the package's
            classes are kept exactly as written AND the live band classes
            (kmd-sheet, from styles/kmd-sheet.css, the same ones A.1, A.2 and
            B use) are carried alongside so the card draws in the page's own
            hand. Not one value was retyped and not one class was renamed. */}
        {/* DP106 Part 5b — the accent rule, 2px in the band's accent, set the
            same way A.1 sets its own (line 556): only the top border, from a
            token, so no raw colour enters this file. */}
        <article className="card c12 kmd-sheet"
                 style={{ '--ac': 'var(--km-info)',
                          borderTopWidth: '2px', borderTopColor: 'var(--km-info)' }}
                 data-testid="card-C1-pipeline">
          <div className="blk kmd-sheet__block">
            <span className="num kmd-sheet__series" style={{ color: 'var(--km-info)' }}>C.1</span>
            <span className="t kmd-sheet__name">Before a project exists</span>
            <span className="r kmd-sheet__meta">inquiry &rarr; signed</span>
          </div>
          <div className="bdy kmd-sheet__body">
            {pipelineStages.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="pipeline-stages-empty">
                No pipeline stages returned.
              </p>
            ) : (
              <div className="three"
                   data-testid="pipeline-stages"
                   style={{ display: 'grid',
                            gridTemplateColumns: `repeat(${pipelineStages.length},1fr)`,
                            gap: 'var(--km-s3)', border: 0, margin: 0, padding: 0 }}>
                {pipelineStages.map((s) => {
                  const count = leads.filter((l) => l.stage === s.id).length;
                  /* Part 4d — signed in the ok colour, not_hired in muted ink,
                     by the stage's own id. Neither id exists on this tenant
                     today; see the report. */
                  const cls = s.id === 'signed' ? 'fig kmd-figure kmd-figure--ok'
                    : s.id === 'not_hired' ? 'fig kmd-figure kmd-figure--muted'
                      : 'fig kmd-figure';
                  return (
                    <div key={s.id}>
                      <span className={`${cls} kmd-figure--sm`}
                            style={s.id === 'not_hired' ? { color: 'var(--km-ink-3)' } : undefined}
                            data-testid={`pipeline-count-${s.id}`}>{count}</span>
                      <span className="figl kmd-figure__label"
                            data-testid={`pipeline-label-${s.id}`}>{s.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </article>

        {/* DP82 Part 9 — the closing line, as My Desk and the Work Table home
            both end. */}
        <p
          className="mt-4 font-mono text-[9px] leading-[1.7] tracking-[0.07em] text-muted-foreground"
          data-testid="projects-closing-note"
        >
          The phase strip filters the catalogue. Every plate carries its project&rsquo;s own
          colour on the number and the progress rule, so the board reads by project before it
          reads by text.
        </p>
      </main>
    </div>
    </AppShell>
  );
}

function ProjectCard({ project, indicators, getStatusColor, formatDate, formatPhone, getGoogleMapsUrl, onArchive, onDelete, onRestore, brandColor, canEditPhotos, onAddPhoto }) {
  // Soft tint derived from the company's brand color (6-digit hex only);
  // falls back to the existing neutral when no brand color is set.
  const tintHex = /^#[0-9a-fA-F]{6}$/.test(brandColor || '') ? brandColor : null;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const clientName = project.client?.name || project.client_name || 'No client';
  const clientPhone = project.client?.phone;
  const clientEmail = project.client?.email;
  const siteAddress = project.site_address;
  const mapsUrl = getGoogleMapsUrl(siteAddress);
  const phoneUrl = formatPhone(clientPhone);
  // DP48c — the badge labelled the card from the fixed six-name list above,
  // which has no entry for a 7th or 8th phase, so an 8-phase project showed
  // PHASE with nothing after it. It reads the project's own phases now.
  const cardPhase = templatePhaseOf(project);
  const currentPhase = cardPhase ? { name: cardPhase, short: phaseShort(cardPhase) } : null;
  const isArchived = project.status === 'archived';
  
  // Extract indicators with defaults
  const openTasks = indicators?.open_tasks || 0;
  const unreadMessages = indicators?.unread_messages || 0;
  const budgetStatus = indicators?.budget_status || 'normal';
  const budgetPercent = indicators?.budget_percent || 0;

  // Build 13.4 (Feb 2026) — prefer the thumbnail everywhere. Also read
  // gallery item thumbnail_url before falling through to full-size url.
  const projectImage = project.primary_image_thumbnail || project.primary_image || project.placeholder_image_thumbnail || project.placeholder_image
    || (Array.isArray(project.images) && (project.images[0]?.thumbnail_url || project.images[0]?.url));
  const initials = `${(project.name || '?').charAt(0)}${(clientName || '?').charAt(0)}`.toUpperCase();
  const addressQuery = encodeURIComponent(
    `${siteAddress?.street || ''} ${siteAddress?.city || ''} ${siteAddress?.state || ''}`.trim()
  );

  return (
    <>
      <div
        className={`relative h-[280px] rounded-lg overflow-hidden cursor-pointer group transition-all duration-300 ${
          project.is_test_fixture
            ? 'border-2 border-dashed border-amber-400 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-500/10'
            : 'border border-border hover:border-[var(--km-brand)]/30 hover:shadow-xl'
        } ${isArchived ? 'opacity-60' : ''}`}
        data-testid={project.is_test_fixture ? 'project-card-fixture' : `project-card-${project.id}`}
        onClick={() => { window.location.href = `/projects/${project.id}/hub`; }}
      >
        {/* Full-bleed background — photo or tinted initials placeholder */}
        {projectImage ? (
          <img
            src={projectImage}
            alt={project.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-start justify-center"
            style={{
              background: tintHex
                ? `linear-gradient(135deg, ${tintHex}14 0%, ${tintHex}33 100%)`
                : 'linear-gradient(135deg, #f5ede8 0%, #e8d5cc 100%)',
            }}
          >
            <div className="mt-12" style={{ fontSize: 56, fontWeight: 700, color: tintHex || '#BF7F3F', opacity: 0.35, letterSpacing: '0.08em' }}>
              {initials}
            </div>
          </div>
        )}

        {/* Dark bottom gradient for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none" />

        {/* Phase Strip - Architectural accent line at top (amber for fixture) */}
        <div className={`absolute top-0 left-0 right-0 h-1 z-10 ${
          project.is_test_fixture
            ? 'bg-gradient-to-r from-amber-400 to-amber-600'
            : 'bg-gradient-to-r from-[var(--km-brand)] to-[var(--km-accent)]'
        }`} />

        {/* Test fixture chip — sits above the more-menu so it's the first thing the eye lands on */}
        {project.is_test_fixture && (
          <div
            className="absolute top-3 left-3 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/95 text-white text-[10px] font-bold uppercase tracking-wider shadow-md"
            data-testid="fixture-chip"
          >
            🧪 Test Fixture
          </div>
        )}

        {/* Top-left: add-photo invite (placeholder cards, staff only) */}
        {!projectImage && canEditPhotos && !isArchived && !project.is_test_fixture && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAddPhoto(project); }}
            className="absolute top-3 left-3 z-20 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 text-white/90 hover:bg-black/50 hover:text-white text-xs font-medium tracking-wide transition-colors"
            title="Add photo"
            aria-label={`Add photo for ${project.name}`}
            data-testid={`add-photo-invite-${project.id}`}
          >
            <Camera className="w-3.5 h-3.5" />
            Add photo
          </button>
        )}

        {/* Top-right: more menu */}
        <div className="absolute top-2.5 right-2.5 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon"
                      className="h-7 w-7 rounded-full bg-black/30 text-white hover:bg-black/50 hover:text-white"
                      onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => window.location.href = `/projects/${project.id}/hub`}>
                <Settings className="w-4 h-4 mr-2" />
                Project Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.location.href = `/projects/${project.id}/hub?tab=clips`}>
                <Layers className="w-4 h-4 mr-2" />
                View Clips
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isArchived ? (
                <DropdownMenuItem onClick={() => onRestore(project.id)}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Restore Project
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onArchive(project.id)}>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive Project
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Bottom: content over the gradient */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10 text-white">
          <div className="font-mono text-[11px] tracking-[0.08em] uppercase text-white/70">
            {project.project_code}
          </div>
          <h3 className="font-heading text-lg font-semibold leading-tight truncate mt-0.5" title={project.name}>
            {project.name}
          </h3>

          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {currentPhase && (
              <span
                data-testid={`phase-badge-${project.id}`}
                title={currentPhase.name}
                className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.08em] uppercase px-1.5 py-0.5 rounded-sm border border-white/30 bg-white/10 backdrop-blur-sm"
              >
                Phase {currentPhase.short}
              </span>
            )}
            {project.status && project.status !== 'active' && (
              <Badge className={`text-[10px] uppercase tracking-wide ${getStatusColor(project.status)}`}>
                {project.status.replace('_', ' ')}
              </Badge>
            )}
            {openTasks > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-[var(--km-brand)]/70 text-white/95"
                    title={`${openTasks} open action item(s)`} data-testid={`tasks-indicator-${project.id}`}>
                <CheckCircle2 className="w-3 h-3" />{openTasks} open
              </span>
            )}
            {budgetStatus === 'warning' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-300"
                    data-testid={`budget-warning-${project.id}`}>
                <DollarSign className="w-3 h-3" />{Math.round(budgetPercent)}%
              </span>
            )}
            {budgetStatus === 'critical' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-300"
                    data-testid={`budget-critical-${project.id}`}>
                <AlertTriangle className="w-3 h-3" />{Math.round(budgetPercent)}%
              </span>
            )}
          </div>

          <div className="text-[13px] text-white/80 mt-2 truncate" title={clientName}>
            <span className="font-medium text-white/95">{clientName}</span>
            {(siteAddress?.city || siteAddress?.state) && (
              <span> · {siteAddress?.city}{siteAddress?.city && siteAddress?.state ? ', ' : ''}{siteAddress?.state}</span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-2 text-[12px]">
            {clientPhone && (
              <a
                href={phoneUrl || `tel:${clientPhone}`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`project-call-${project.id}`}
                className="inline-flex items-center gap-1.5 text-white/85 hover:text-white transition-colors"
              >
                <Phone className="w-3.5 h-3.5" /> {clientPhone}
              </a>
            )}
            {siteAddress?.street && (
              <a
                href={mapsUrl || `https://maps.google.com/?q=${addressQuery}`}
                target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`project-directions-btn`}
                className="inline-flex items-center gap-1.5 text-white/85 hover:text-white transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" /> Directions
              </a>
            )}
            <span className="inline-flex items-center gap-1 text-[11px] text-white/60 ml-auto">
              <Clock className="w-3 h-3" /> {formatDate(project.updated_at)}
            </span>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{project.name}&quot; and all associated data including clips, estimates, and floor plans. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                onDelete(project.id);
                setShowDeleteConfirm(false);
              }}
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


class DashboardErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Dashboard crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center p-8">
            <h2 className="text-xl font-heading mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">{this.state.error?.message}</p>
            <button 
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-4 py-2 bg-[var(--km-brand)] text-white rounded hover:opacity-90"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DashboardWithErrorBoundary() {
  return (
    <DashboardErrorBoundary>
      <Dashboard />
    </DashboardErrorBoundary>
  );
}
